import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getProductDetails } from '../services/api';
import { ProductListing } from '../types';

const DEFAULT_CRAIGSLIST_IMAGE = 'https://res.cloudinary.com/dro7c1ps1/image/upload/v1763328960/logo_orzuvp.png';

/**
 * Hook to automatically fetch real images for Craigslist listings that have default images
 */
export function useCraigslistImageLoader(listings: ProductListing[]) {
  const { userId, user } = useAuth();
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [updatedListings, setUpdatedListings] = useState<ProductListing[]>(listings);
  const fetchedListingIds = useRef<Set<string>>(new Set());
  const previousListingsRef = useRef<string>('');

  // Update listings when they change
  useEffect(() => {
    // Create a signature of the listings to detect when the list actually changes
    const listingsSignature = listings.map(l => l.id).join(',');
    
    // Only update if the listings have actually changed (new search results)
    if (listingsSignature !== previousListingsRef.current) {
      setUpdatedListings(listings);
      previousListingsRef.current = listingsSignature;
      // Reset fetched tracking when we get new listings
      fetchedListingIds.current.clear();
    }
  }, [listings]);

  // Check if a listing needs image fetching
  const needsImageFetch = useCallback((listing: ProductListing): boolean => {
    if (listing.marketplace !== 'Craigslist') {
      return false;
    }
    
    const images = listing.images || [];
    if (images.length === 0) {
      return true;
    }
    
    // Check if the first image is the default image
    return images[0] === DEFAULT_CRAIGSLIST_IMAGE;
  }, []);

  // Fetch images for a single listing
  const fetchListingImages = useCallback(async (listing: ProductListing) => {
    const listingId = listing.id;
    
    // Mark as loading
    setImageLoadingStates(prev => ({ ...prev, [listingId]: true }));

    try {
      // For Craigslist, use the URL instead of product_id
      const productId = listing.url || listingId;
      const details = await getProductDetails(
        productId,
        listing.marketplace,
        'craigslist',
        userId,
        userEmail
      );

      // Update the listing with new images if we got them
      if (details && details.images && details.images.length > 0) {
        const hasRealImages = details.images.some(
          img => img && img !== DEFAULT_CRAIGSLIST_IMAGE
        );

        if (hasRealImages) {
          setUpdatedListings(prev => 
            prev.map(l => 
              l.id === listingId 
                ? { ...l, images: details.images }
                : l
            )
          );
        }
      }
    } catch (error) {
      console.error(`Failed to fetch images for listing ${listingId}:`, error);
      // Keep the default image on error
    } finally {
      // Mark as done loading
      setImageLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[listingId];
        return newState;
      });
    }
  }, [userId, userEmail]);

  // Automatically fetch images for listings that need them
  useEffect(() => {
    const listingsToFetch = updatedListings.filter(listing => {
      // Only fetch if it needs images AND we haven't already fetched it
      return needsImageFetch(listing) && !fetchedListingIds.current.has(listing.id);
    });
    
    // Fetch images for each listing (with a small delay to avoid overwhelming the API)
    listingsToFetch.forEach((listing, index) => {
      // Mark as fetched immediately to prevent duplicate requests
      fetchedListingIds.current.add(listing.id);
      
      // Stagger requests slightly to avoid rate limiting
      setTimeout(() => {
        fetchListingImages(listing);
      }, index * 200); // 200ms delay between each request
    });
  }, [updatedListings, needsImageFetch, fetchListingImages]);

  const isImageLoading = useCallback((listingId: string): boolean => {
    return imageLoadingStates[listingId] === true;
  }, [imageLoadingStates]);

  return {
    listings: updatedListings,
    isImageLoading,
  };
}

